import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardFooter,
  Button,
  Input,
  Select,
  SelectItem
} from '@heroui/react';
import type { CompanyData } from '@/types/company.model';
import { updateCompanyInfo } from '@/services/empresa/company.service';
import { uploadFile } from '@/services/api/upload.service';
import { FotoUpload } from '../shared/FotoUpload';

interface CompanyConfigFormProps {
  companyData: CompanyData;
  onCancel: () => void;
  isLoading?: boolean;
}

const CompanyConfigForm: React.FC<CompanyConfigFormProps> = ({
  companyData,
  onCancel,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<CompanyData>({ 
    ...companyData,
    logo: companyData.logo || null
  });

  const [file, setFile] = useState<File|null>(null)


  const handleChange = (field: keyof CompanyData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!companyData) return;

        try {
          
          // Handle logo upload if a new logo has been selected
          let data = { ...formData };
              try {
                if(file){
                  const uploadedFile = await uploadFile(file);
                  data = {
                    ...data,
                    logo: uploadedFile[0].id
                  };
                }
                
                // Update the company data with the new logo ID
              } catch (uploadError) {
                console.error('Error uploading logo:', uploadError);
                // Continue with saving the company data even if logo upload fails
              }                    
          const finalData = await updateCompanyInfo(data);
          if(finalData){
            alert("Empresa Actualizada")
          }

        } catch (err) {
          console.error('Error updating company info:', err);
        } finally {

        }


  };

  // Tamaño options
  const tamanoOptions = [
    { key: '1-10', value: '1-10 empleados' },
    { key: '11-50', value: '11-50 empleados' },
    { key: '51-200', value: '51-200 empleados' },
    { key: '200+', value: 'Más de 200 empleados' },
  ];

  // Industria options
  const industriaOptions = [
    { key: '', value: 'Selecciona una industria' },
    { key: 'Agricultura, ganadería, silvicultura y pesca', value: 'Agricultura, ganadería, silvicultura y pesca' },
    { key: 'Minería y extracción', value: 'Minería y extracción' },
    { key: 'Manufactura e industria pesada', value: 'Manufactura e industria pesada' },
    { key: 'Energía y servicios públicos', value: 'Energía y servicios públicos' },
    { key: 'Construcción e infraestructura', value: 'Construcción e infraestructura' },
    { key: 'Transporte y logística', value: 'Transporte y logística' },
    { key: 'Comercio mayorista y minorista', value: 'Comercio mayorista y minorista' },
    { key: 'Tecnología de la información y comunicaciones (TIC)', value: 'Tecnología de la información y comunicaciones (TIC)' },
    { key: 'Servicios financieros y seguros', value: 'Servicios financieros y seguros' },
    { key: 'Bienes raíces y propiedad', value: 'Bienes raíces y propiedad' },
    { key: 'Salud y servicios sociales', value: 'Salud y servicios sociales' },
    { key: 'Educación y formación profesional', value: 'Educación y formación profesional' },
    { key: 'Turismo, hotelería y gastronomía', value: 'Turismo, hotelería y gastronomía' },
    { key: 'Medios, arte y entretenimiento', value: 'Medios, arte y entretenimiento' },
    { key: 'Servicios profesionales, científicos y técnicos', value: 'Servicios profesionales, científicos y técnicos' },
    { key: 'Administración pública y defensa', value: 'Administración pública y defensa' },
    { key: 'Organizaciones sin fines de lucro y ONG', value: 'Organizaciones sin fines de lucro y ONG' },
    { key: 'Servicios personales y domésticos', value: 'Servicios personales y domésticos' },
    { key: 'Sector ambiental y sostenibilidad', value: 'Sector ambiental y sostenibilidad' },
    { key: 'Investigación y desarrollo (I+D)', value: 'Investigación y desarrollo (I+D)' },
  ];

  function fileChangeListener(file: File): void {
    try {
     setFile(file);
    } catch (error) {
      throw error;
    }    
  }

  return (
    <Card className="w-full">
      <CardBody className="py-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nombre de la Empresa"
              placeholder="Ingrese el nombre de la empresa"
              value={formData.nombre}
              onChange={(e) => handleChange('nombre', e.target.value)}
              required
            />
            <Input
              label="Razón Social"
              placeholder="Ingrese la razón social"
              value={formData.razonSocial}
              onChange={(e) => handleChange('razonSocial', e.target.value)}
              required
            />
            <Select
              label="Tipo de Identificación"
              placeholder="Seleccione el tipo de identificación"
              selectedKeys={formData.tipoIdentificacion ? [formData.tipoIdentificacion] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys).join('');
                handleChange('tipoIdentificacion', value);
              }}
              required
            >
              <SelectItem key="CC">Cédula de Ciudadanía</SelectItem>
              <SelectItem key="CE">Cédula de Extranjería</SelectItem>
              <SelectItem key="NIT">NIT</SelectItem>
              <SelectItem key="PP">Pasaporte</SelectItem>
              <SelectItem key="TI">Tarjeta de Identidad</SelectItem>
            </Select>
            <Input
              label="Número de Identificación"
              placeholder="Ingrese el número de identificación"
              value={formData.numeroIdentificacion}
              onChange={(e) => handleChange('numeroIdentificacion', e.target.value)}
              required
            />
            <Input
              label="Dirección"
              placeholder="Ingrese la dirección"
              value={formData.direccion || ''}
              onChange={(e) => handleChange('direccion', e.target.value)}
            />
            <Input
              label="Teléfono"
              placeholder="Ingrese el teléfono"
              value={formData.telefono}
              onChange={(e) => handleChange('telefono', e.target.value)}
              required
            />
            <Input
              label="Email"
              placeholder="Ingrese el email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
            />
            <Input
              label="Email de Contacto"
              placeholder="Ingrese el email de contacto"
              type="email"
              value={formData.emailContacto || ''}
              onChange={(e) => handleChange('emailContacto', e.target.value)}
            />
            <Input
              label="Persona de Contacto"
              placeholder="Ingrese la persona de contacto"
              value={formData.personaContacto || ''}
              onChange={(e) => handleChange('personaContacto', e.target.value)}
            />
            <Select
              label="Tamaño de la Empresa"
              placeholder="Seleccione el tamaño de la empresa"
              selectedKeys={formData.tamano ? [formData.tamano] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys).join('');
                handleChange('tamano', value);
              }}
              required
            >
              {tamanoOptions.map((option) => (
                <SelectItem key={option.key}>
                  {option.value}
                </SelectItem>
              ))}
            </Select>
            <Select
              label="Industria"
              placeholder="Seleccione la industria"
              selectedKeys={formData.industria ? [formData.industria] : []}
              onSelectionChange={(keys) => {
                const value = Array.from(keys).join('');
                handleChange('industria', value);
              }}
              required
            >
              {industriaOptions.map((option) => (
                <SelectItem key={option.key}>
                  {option.value}
                </SelectItem>
              ))}
            </Select>
          </div>

          {/* Logo Upload Section */}
          <div className="mt-6">
            <h3 className="text-lg font-medium mb-4">Logo de la Empresa</h3>
            <FotoUpload onFileChange={fileChangeListener} initialImageUrl={typeof formData.logo !== 'number' ? formData.logo : null}></FotoUpload>
          </div>
        </form>
      </CardBody>
      <CardFooter className="flex justify-end gap-2">
        <Button variant="light" onPress={onCancel}>
          Cancelar
        </Button>
        <Button 
          color="primary" 
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={isLoading}
        >
          Guardar Cambios
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CompanyConfigForm;